import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi } from '../../api';
import { Loan, LoanStatus } from '../../types';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Button } from '../../components/Button';
import { formatCurrency } from '../../utils/currency';

export const LoansList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loans, setLoans] = useState<Loan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');

  useEffect(() => {
    fetchLoans();
  }, [currentPage, statusFilter]);

  const fetchLoans = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getLoans(currentPage, 20, statusFilter || undefined);
      if (response.success && response.data) {
        setLoans(response.data);
        setTotalPages(response.pagination.totalPages);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load loans');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1);
    if (status) {
      setSearchParams({ status });
    } else {
      setSearchParams({});
    }
  };

  const getStatusClass = (status: LoanStatus) => {
    switch (status) {
      case LoanStatus.ACTIVE:
        return 'status-success';
      case LoanStatus.PAID_OFF:
        return 'status-info';
      case LoanStatus.DEFAULTED:
      case LoanStatus.WRITTEN_OFF:
        return 'status-danger';
      default:
        return 'status-secondary';
    }
  };

  if (loading && loans.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="loans-list-page">
      <div className="page-header">
        <h1>All Loans</h1>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}

      <div className="filters">
        <div className="filter-group">
          <label htmlFor="status">Filter by Status:</label>
          <select
            id="status"
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value={LoanStatus.ACTIVE}>Active</option>
            <option value={LoanStatus.PAID_OFF}>Paid Off</option>
            <option value={LoanStatus.DEFAULTED}>Defaulted</option>
            <option value={LoanStatus.WRITTEN_OFF}>Written Off</option>
          </select>
        </div>
      </div>

      {loans.length === 0 ? (
        <div className="empty-state">
          <p>No loans found.</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Customer</th>
                  <th>Product</th>
                  <th>Principal</th>
                  <th>Outstanding</th>
                  <th>Total Paid</th>
                  <th>Status</th>
                  <th>Disbursed</th>
                  <th>Maturity</th>
                </tr>
              </thead>
              <tbody>
                {loans.map((loan) => (
                  <tr key={loan.loan_id}>
                    <td>{loan.loan_reference}</td>
                    <td>{loan.product_name || 'N/A'}</td>
                    <td>{loan.product_name || 'N/A'}</td>
                    <td>
                      {formatCurrency(loan.principal_amount)}
                    </td>
                    <td>
                      {formatCurrency(
                        parseFloat(loan.outstanding_principal) +
                        parseFloat(loan.outstanding_interest)
                      )}
                    </td>
                    <td>
                      {formatCurrency(loan.total_paid)}
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusClass(loan.status)}`}>
                        {loan.status}
                        {loan.is_overdue && ' (Overdue)'}
                      </span>
                    </td>
                    <td>{new Date(loan.disbursement_date).toLocaleDateString()}</td>
                    <td>{new Date(loan.maturity_date).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <Button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                variant="secondary"
              >
                Previous
              </Button>
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                variant="secondary"
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
